// The templates' form kit (spec G14-templates, "The form panel"): what every template's form is
// made of, as live's widgets share it: the form's props, live's image and video slots, and the
// round start.
import {
  ActionIcon,
  CloseButton,
  FileButton,
  Image,
  Loader,
  Menu,
  Switch,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  Archive,
  ArrowUp,
  Image as ImageIcon,
  ImagePlus,
  Library,
  LibraryBig,
  Package,
  Sparkles,
  Upload,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import type {
  Asset,
  AssetRef,
  ExampleRef,
  Job,
  Product,
  ProductImage,
  TemplateCard,
  TemplateExample,
  TemplateOptionView,
  TemplateSource,
  TemplateVoice,
} from "../../../api/generated/model";
import { MediaMime } from "../../../api/generated/zod";
import { useGenerateImage } from "../../../platform/media/GenerateImageDialog";
import {
  IMAGE_TYPES,
  ImagePicker,
  pictureOf,
  type PickedImage,
  type PickerTab,
} from "../../../platform/media/ImagePicker";
import { ProductListPopover, type ListedProduct } from "../../../platform/media/ProductListPopover";
import { ProductPicker } from "../../../platform/media/ProductPicker";
import { SourceTool, type SourceToolView } from "../../../ui/SourceTool";
import { UploadState, useImageUpload } from "../../../platform/media/image-upload";
import { ReferencesDialog } from "../../../platform/media/ReferencesDialog";
import { selectUpTo } from "../../../ui/format";
import { VideoFrame } from "../../../ui/VideoFrame";
import classes from "../templates.module.css";

/** What the panel hands a template's form. */
export interface TemplateFormProps {
  teamId: string;
  brandId: string;
  template: TemplateCard;
  /** Live's pill above the form (the medium's icon, the title, the panel's x). */
  above: React.ReactNode;
  /** Open with the template's example filled in (Replicate). */
  replicate: boolean;
  /** The card's example, read with its detail: none for a card without one (or one not read). */
  example: FormExample | null;
  /** The choices the form offers of its own (Hooks' hooks), read with the card's detail. */
  options: readonly TemplateOptionView[];
  onStarted: (job: Job) => void;
}

/**
 * The card's example as a form fills from it (G14, live's "Reuse example"): its inputs, and each
 * example input's signed URL by the key its `ExampleRef` names.
 */
export interface FormExample {
  inputs: TemplateExample;
  urls: Readonly<Record<string, string>>;
  /** The voices it speaks with (Actor Swap's template voices, in no list), to name and play. */
  voices: readonly TemplateVoice[];
}

/** A card's example input in a slot: the catalog's file, which the run uses where it lies. */
export interface ExampleImage {
  exampleKey: string;
  url: string | null;
  name: string;
}

/** What a slot holds: a library file or a product's photo, or the card's example input. */
export type SlotImage = PickedImage | ExampleImage;

/** The slot's picture as the template's inputs name it. */
export function slotSource(image: SlotImage): AssetRef | ExampleRef {
  return "exampleKey" in image
    ? { kind: "example", key: image.exampleKey }
    : { kind: "asset", asset_id: image.assetId };
}

/** A picture's identity in a list of them (the inspirations'). */
export function slotId(image: SlotImage): string {
  return "exampleKey" in image ? image.exampleKey : image.assetId;
}

/** An example's picture as a slot shows it; a source of another kind never fills an example. */
export function exampleImage(example: FormExample, source: TemplateSource): ExampleImage | null {
  if (source.kind !== "example") return null;
  return { exampleKey: source.key, url: example.urls[source.key] ?? null, name: "Example" };
}

/**
 * Live's "Reuse example" switch (`ReuseExampleSwitch`): on fills the form with the card's
 * example, off clears it; off and disabled while the card has none. Replicate opens it on.
 * `locked` disables it while the form makes something the example would drop (Actor Swap's
 * actors in the making, live's `!hasActorGenerations`).
 */
export function ReuseSwitch(props: {
  example: FormExample | null;
  checked: boolean;
  onChange: (on: boolean) => void;
  locked?: boolean | undefined;
}) {
  return (
    <Switch
      label="Reuse example"
      checked={props.checked}
      disabled={props.example === null || props.locked === true}
      onChange={(event) => props.onChange(event.currentTarget.checked)}
    />
  );
}

/** Live's hint on an image slot's empty tile (`SOURCE_IMAGE_TOOLTIP`): how its four sources work. */
export const SOURCE_IMAGE_TOOLTIP =
  "The source image for this template. Choose one input method: pick from your Library, select a " +
  "Product image, upload a file, or generate one on the spot with the Generate modal — only one of " +
  "these can be used at a time.";

export interface ImageSlotProps {
  teamId: string;
  brandId: string;
  /** Live's slot label: "Image". */
  label: string;
  /** Live's line under the library dialog's "Select image": "Pick the image to place on a new
   * background". */
  title: string;
  value: SlotImage | null;
  onChange: (value: SlotImage | null) => void;
  /**
   * The picker's tabs, opened at once by the empty tile: Static Ad Generation's inspirations,
   * live's reference modal. Left out, the tile opens live's four sources instead.
   */
  tabs?: readonly PickerTab[];
  /** Live's tooltip on the empty slot: its four sources' unless the form words its own. */
  tooltip?: string;
}

/**
 * Live's image slot (`ImageSourceSlot`, `ImageSourceField`): a square naming what goes in it,
 * which opens live's menu of its four sources, one at a time (capture templates-video/44, issue
 * 739): From library ("Select image", capture 45), Generate (live's Generate image dialog, the
 * Studio's simple image, saved to the library), Upload (into the library), From product (one of a
 * product's photos). Given `tabs`, the tile opens that picker instead. Once filled, the picture
 * with an x.
 */
export function ImageSlot(props: ImageSlotProps) {
  const { label, value, onChange } = props;
  if (value !== null) {
    return (
      <span className={classes.slotFilled}>
        <Image src={value.url} alt={value.name} className={classes.slotPicture} />
        <CloseButton
          size="xs"
          className={classes.slotRemove}
          aria-label={`Remove the ${label.toLowerCase()}`}
          onClick={() => onChange(null)}
        />
      </span>
    );
  }
  return props.tabs === undefined ? <SourceSlot {...props} /> : <PickerSlot {...props} tabs={props.tabs} />;
}

/** The empty tile that opens the picker on its tabs (Static Ad Generation's inspirations). */
function PickerSlot(props: ImageSlotProps & { tabs: readonly PickerTab[] }) {
  const { teamId, brandId, label, title, onChange, tabs, tooltip } = props;
  const [picking, setPicking] = useState(false);
  return (
    <>
      <Tooltip label={tooltip} disabled={tooltip === undefined} multiline maw={280} withArrow>
        <UnstyledButton className={classes.slot} aria-label={label} onClick={() => setPicking(true)}>
          <ImagePlus size={20} strokeWidth={1.5} aria-hidden />
          <Text size="xs">{label}</Text>
        </UnstyledButton>
      </Tooltip>
      <ImagePicker
        library={{ teamId, brandId }}
        title={title}
        opened={picking}
        onClose={() => setPicking(false)}
        onPick={onChange}
        tabs={tabs}
      />
    </>
  );
}

/** A library file as the slot holds it: a generated or an uploaded picture. */
function slotPicture(asset: Asset): PickedImage {
  return { assetId: asset.id, url: pictureOf(asset), name: asset.name };
}

/** The empty tile with live's menu of four sources (`ImageSourceSlot`). */
function SourceSlot(props: ImageSlotProps) {
  const { teamId, brandId, label, title, onChange } = props;
  const tooltip = props.tooltip ?? SOURCE_IMAGE_TOOLTIP;
  const [source, setSource] = useState<"library" | "product" | null>(null);
  const resetChooser = useRef<() => void>(null);
  const running = useRef<AbortController | null>(null);
  useEffect(() => () => running.current?.abort(), []);
  const upload = useImageUpload({
    teamId,
    brandId,
    onUploaded: (asset) => onChange(slotPicture(asset)),
    onUploading: (controller) => {
      running.current = controller;
    },
  });
  const generate = useGenerateImage({ teamId, brandId, onSaved: (asset) => onChange(slotPicture(asset)) });
  const tile = (
    <UnstyledButton className={classes.slot} aria-label={label} aria-busy={upload.pending}>
      {upload.pending ? <Loader size={20} aria-hidden /> : <ImageIcon size={20} strokeWidth={1.5} aria-hidden />}
      <Text size="xs">{label}</Text>
    </UnstyledButton>
  );
  return (
    <>
      <ProductPicker
        teamId={teamId}
        brandId={brandId}
        chosen={null}
        subtitle={selectUpTo(1, "product")}
        typography={false}
        colors={false}
        assets={false}
        preselect={mainPicture}
        opened={source === "product"}
        onClose={() => setSource(null)}
        onConfirm={(choice) => onChange({ assetId: choice.assetId, url: choice.url, name: choice.name })}
        anchor={(wrap) =>
          wrap(
            <span className={classes.slotFilled}>
              <FileButton
                accept={IMAGE_TYPES.join(",")}
                resetRef={resetChooser}
                onChange={(file) => {
                  void upload.send(file);
                  resetChooser.current?.();
                }}
                inputProps={{ "aria-label": `Choose an ${label.toLowerCase()} to upload` }}
              >
                {(chooser) => (
                  <Menu position="bottom-start">
                    <Menu.Target>
                      <Tooltip label={tooltip} multiline maw={280} withArrow>
                        {tile}
                      </Tooltip>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item leftSection={<Library size={16} strokeWidth={1.5} />} onClick={() => setSource("library")}>
                        From library
                      </Menu.Item>
                      <Menu.Item leftSection={<Sparkles size={16} strokeWidth={1.5} />} onClick={generate.open}>
                        Generate
                      </Menu.Item>
                      <Menu.Item leftSection={<Upload size={16} strokeWidth={1.5} />} onClick={chooser.onClick}>
                        Upload
                      </Menu.Item>
                      <Menu.Item leftSection={<Package size={16} strokeWidth={1.5} />} onClick={() => setSource("product")}>
                        From product
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </FileButton>
            </span>,
          )
        }
      />
      <UploadState upload={upload} />
      {source === "library" ? (
        <ReferencesDialog
          teamId={teamId}
          brandId={brandId}
          title="Select image"
          subtitle={title}
          max={1}
          chosen={[]}
          onClose={() => setSource(null)}
          onConfirm={(chosen) => onChange(chosen[0] ?? null)}
        />
      ) : null}
      {generate.dialog}
    </>
  );
}

/** A card's example clip in a video slot (Actor Swap's hook): the catalog's file, used where it
 * lies, its length known. */
export interface ExampleVideo {
  exampleKey: string;
  url: string | null;
  name: string;
  durationMs: number | null;
}

/** What a video slot holds: a library video, or the card's example clip. */
export type SlotVideo = PickedImage | ExampleVideo;

/** The slot's video as the template's inputs name it. */
export function videoSource(video: SlotVideo): AssetRef | ExampleRef {
  return "exampleKey" in video
    ? { kind: "example", key: video.exampleKey }
    : { kind: "asset", asset_id: video.assetId };
}

/** The library's video types, which a video slot's upload takes. */
const VIDEO_TYPES = MediaMime.options.filter((mime) => mime.startsWith("video/"));

export interface VideoSlotProps {
  teamId: string;
  brandId: string;
  /** Live's slot label: "Video", or Actor Swap's "Talking head". */
  label: string;
  icon: ReactNode;
  /** The line under live's "Select video": what the pick is for. */
  subtitle: string;
  value: SlotVideo | null;
  onChange: (value: SlotVideo | null) => void;
  /** Live's tooltip on the empty slot, where its form gives one. */
  tooltip?: string;
  /** Live's problem with the picked video: its ring, and the words on hover. */
  problem?: string | null;
  /** Live's media row's tile (Actor Swap's, as large as an actor's card), not the 90 x 72 slot. */
  tile?: boolean;
  /** The voiceover forms' tile, live's 116 x 148 (`media-tiles`): Talking Head Overlay's. */
  tall?: boolean;
  /** Greyed, as live's while its form is busy (Talking Head Overlay's voiceover being made). */
  disabled?: boolean;
}

/**
 * Live's video slot (`VideoSourceSlot`): empty, a square naming what goes in it whose menu picks
 * From library (live's "Select video") or uploads into the library; once filled, the video's
 * still with an x, ringed and its problem on hover where it has one.
 */
export function VideoSlot(props: VideoSlotProps) {
  const { teamId, brandId, label, icon, subtitle, value, onChange, tooltip, problem = null } = props;
  const tile = props.tile === true || props.tall === true || undefined;
  const tall = props.tall === true || undefined;
  const disabled = props.disabled === true;
  const [picking, setPicking] = useState(false);
  const resetChooser = useRef<() => void>(null);
  const running = useRef<AbortController | null>(null);
  useEffect(() => () => running.current?.abort(), []);
  const upload = useImageUpload({
    teamId,
    brandId,
    kind: "video",
    onUploaded: (asset) =>
      onChange({
        assetId: asset.id,
        url: asset.urls.poster ?? null,
        name: asset.name,
        width: asset.width,
        height: asset.height,
        durationMs: asset.duration_ms,
        bytes: asset.bytes,
      }),
    onUploading: (controller) => {
      running.current = controller;
    },
  });
  const empty = (
    <UnstyledButton
      className={classes.slot}
      data-tile={tile}
      data-tall={tall}
      aria-label={label}
      aria-busy={upload.pending}
      disabled={disabled}
    >
      {upload.pending ? <Loader size={20} aria-hidden /> : icon}
      <Text size="xs">{label}</Text>
    </UnstyledButton>
  );
  return (
    <>
      {value === null ? (
        <FileButton
          accept={VIDEO_TYPES.join(",")}
          resetRef={resetChooser}
          onChange={(file) => {
            void upload.send(file);
            resetChooser.current?.();
          }}
          inputProps={{ "aria-label": `Choose a ${label.toLowerCase()} video to upload` }}
        >
          {(chooser) => (
            <Menu position="top-start">
              <Menu.Target>
                <Tooltip label={tooltip} disabled={tooltip === undefined} multiline maw={280} withArrow>
                  {empty}
                </Tooltip>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<LibraryBig size={16} />} onClick={() => setPicking(true)}>
                  From library
                </Menu.Item>
                <Menu.Item leftSection={<Upload size={16} />} onClick={chooser.onClick}>
                  Upload
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </FileButton>
      ) : (
        <Tooltip label={problem} disabled={problem === null} multiline maw={280} withArrow>
          <span
            className={classes.slotFilled}
            data-tile={tile}
            data-tall={tall}
            data-invalid={problem === null ? undefined : true}
          >
            {value.url === null ? (
              <span className={classes.slotNoStill}>{value.name}</span>
            ) : "exampleKey" in value ? (
              // The example clip has no still of its own: its first frame, muted, never played.
              <span className={classes.slotPicture}>
                <VideoFrame
                  src={value.url}
                  label={value.name}
                  still
                  fallback={<span className={classes.slotNoStill}>{value.name}</span>}
                />
              </span>
            ) : (
              <Image src={value.url} alt={value.name} className={classes.slotPicture} />
            )}
            <CloseButton
              size="xs"
              className={classes.slotRemove}
              aria-label={`Remove the ${label.toLowerCase()}`}
              disabled={disabled}
              onClick={() => onChange(null)}
            />
          </span>
        </Tooltip>
      )}
      <UploadState upload={upload} />
      {picking ? (
        <ReferencesDialog
          teamId={teamId}
          brandId={brandId}
          kind="video"
          title="Select video"
          subtitle={subtitle}
          max={1}
          chosen={value === null || "exampleKey" in value ? [] : [value.assetId]}
          onClose={() => setPicking(false)}
          onConfirm={(chosen) => onChange(chosen[0] ?? null)}
        />
      ) : null}
    </>
  );
}

/** Live's round ↑ at the footer's end, off until the form is complete. */
export function StartArrow(props: { canStart: boolean; starting: boolean; onStart: () => void }) {
  return (
    <ActionIcon
      variant="filled"
      size="lg"
      radius="100%"
      aria-label="Generate"
      disabled={!props.canStart}
      loading={props.starting}
      onClick={props.onStart}
    >
      <ArrowUp size={20} />
    </ActionIcon>
  );
}

/** The picture a template takes of a product, live's `getMainProductImage`: its product shot,
 * else its main picture, else its first; null for a product with none. */
export function mainPicture(product: Product): ProductImage | null {
  return (
    product.images.find((each) => each.tag === "product_shot") ??
    product.images.find((each) => each.is_main === true) ??
    product.images[0] ??
    null
  );
}

/** A product as the footer's product tool shows it: its name and the picture the form takes. */
export function productView(product: Product): SourceToolView {
  return { name: product.name, url: mainPicture(product)?.url ?? null };
}

export interface ProductToolProps {
  teamId: string;
  brandId: string;
  /** Live's tooltip, the form's own words. */
  tip: string;
  /** The product chosen, as the tool shows it; null for none. */
  view: SourceToolView | null;
  /** The brand's product chosen, which the list marks; null for none or the example's. */
  chosenId: string | null;
  disabled?: boolean;
  onPick: (product: ListedProduct) => void;
  onRemove: () => void;
}

/**
 * Live's product field in a template's footer (`SingleProductSelect`): an archive icon with the
 * form's tooltip, opening the Studio's Products popover ("You can select up to 1 product", a
 * search by name, the brand's products with their photo), closed on a pick. Once it holds a
 * product the icon turns active, with its picture on hover and an x that clears it.
 */
export function ProductTool(props: ProductToolProps) {
  const { teamId, brandId, tip, view, chosenId, onPick, onRemove } = props;
  const [opened, setOpened] = useState(false);
  return (
    <ProductListPopover
      teamId={teamId}
      brandId={brandId}
      chosenId={chosenId}
      opened={opened}
      onClose={() => setOpened(false)}
      onPick={onPick}
      position="top-start"
      anchor={(wrap) => (
        <SourceTool
          label="Products"
          tip={tip}
          icon={<Archive size={20} strokeWidth={1.5} />}
          view={view}
          removeLabel="Remove the product"
          onRemove={onRemove}
          onClick={() => setOpened((open) => !open)}
          open={opened}
          disabled={props.disabled === true}
          target={wrap}
        />
      )}
    />
  );
}
